import React, { useState, useEffect, useCallback } from 'react';
import { useAppSelector } from '../../../../redux/hooks';
import { getHealthCheck, getHealthCheckDetails } from './request/request';
import Switch from '@mui/material/Switch';
import * as XLSX from 'xlsx';
import HealthCheckTable from './components/HealthCheckTable';
import HealthCheckCustomModal from './components/HealthCheckCustomModal';
import CustomModalStatus from './components/CustomModalStatus';
import './styles/CTXRegisterStyles.css';
import { useDispatch } from 'react-redux';
import { SetCloud } from '../../../../redux/feature/CloudSlice';

type ProcessStatus = {
    active: number;
    total: number;
} | null;

type HealthCheckRecord = {
    uuid: string;
    instance: string;
    cpu: number | null;
    ram: number | null;
    storage: number | null;
    processStatus: ProcessStatus;
    portStatus: string | null;
    alerts: string | null;
    mainStatus: string;
};

const HealthCheck: React.FC = () => {
    const TenantId = useAppSelector(state => state.TenantSlice.id);
    const Id = useAppSelector(state => state.SuscriptionSlice.id)
    const Cloud = useAppSelector(state => state.CloudSlice.Type);
    const Region = useAppSelector(state => state.RegionSlice.name);
    const dispatch = useDispatch();
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [records, setRecords] = useState<HealthCheckRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [previousAutoRefresh, setPreviousAutoRefresh] = useState(true);
    const [selectedDetails, setSelectedDetails] = useState<any>(null);
    const [selectedRecord, setSelectedRecord] = useState<HealthCheckRecord | null>(null);
    const [selectedCell, setSelectedCell] = useState<string | null>(null);
    const [chartData, setChartData] = useState<number[]>([]);
    const [chartPlot, setChartPlot] = useState<string[]>([]);
    const [additionalData, setAdditionalData] = useState<any[]>([]);

    //Settear el estado Global del Cloud a un string vacío para el GET
    useEffect(() => {
        if (!Cloud || Cloud === "CloudGlobal") {
            dispatch(SetCloud(""));
        }
    }, [Cloud, dispatch]);

    // Estado para manejar la selección del proceso
    const [showProcessSelector, setShowProcessSelector] = useState(false);
    const [selectedProcess, setSelectedProcess] = useState<string | null>(null);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await getHealthCheck(Id, TenantId, Cloud, Region);
            const data = response.data.data.map((item: any) => ({
                uuid: item.id,
                instance: item.name,
                cpu: item.cpu,
                ram: item.ram,
                storage: item.storage,
                processStatus: item.processStatus,
                portStatus: item.ports,
                alerts: item.date,
                mainStatus: item.mainStatus,
            }));
            setRecords(data);
        } catch (error) {
            setError('Error loading data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        if (autoRefresh) {
            const interval = setInterval(fetchData, 60000);
            return () => clearInterval(interval);
        }
    }, [Id, TenantId, Cloud, Region, autoRefresh]);

    useEffect(() => {
        if (showModal && selectedRecord && selectedCell === 'CPU') {
            const endDate = new Date().toISOString().split('T')[0];
            const startDate = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            const limit = 5;
            handleModalConfirm(startDate, endDate, limit);
        }
    }, [showModal, selectedRecord, selectedCell]);


    // Función para manejar la selección del proceso
    const handleProcessSelect = (process: string) => {
        setSelectedProcess(process);
        setShowProcessSelector(false);
        setShowModal(true);
    };

    const handleRowClick = useCallback((record: HealthCheckRecord, cell: string) => {
        if (cell === 'Process Status' && record.processStatus) {
            const processes = ['process_bgp', 'process_sync', 'process_ha1', 'process_ha2'];
            setSelectedRecord(record);
            setSelectedCell(cell);
            setPreviousAutoRefresh(autoRefresh);
            setAutoRefresh(false);
            setShowProcessSelector(true);
        } else {
            setSelectedRecord(record);
            setSelectedCell(cell);
            setPreviousAutoRefresh(autoRefresh);
            setShowModal(true);
            setAutoRefresh(false);
        }
    }, [autoRefresh]);

    const handleModalClose = () => {
        setShowModal(false);
        setAutoRefresh(previousAutoRefresh);
        setChartData([]);
        setChartPlot([]);
        setAdditionalData([]);
    };

    const handleModalConfirm = async (startDate: string, endDate: string, limit: number) => {
        if (selectedRecord) {
            try {
                const response = await getHealthCheckDetails(Id,
                    selectedRecord.uuid,
                    TenantId,
                    startDate,
                    endDate,
                    limit
                );

                const data = response.data.data;

                let chartData: any[] = [];
                let chartPlot: string[] = [];
                let additionalData: any[] = [];

                switch (selectedCell) {
                    case 'CPU':
                        chartData = data.map((item: any) => item.cpu);
                        chartPlot = data.map((item: any) => new Date(item.createdAt).toLocaleString());
                        break;
                    case 'RAM':
                        chartData = data.map((item: any) => item.ram.percent);
                        chartPlot = data.map((item: any) => new Date(item.createdAt).toLocaleString());
                        additionalData = data.map((item: any) => ({
                            free: item.ram.free,
                            used: item.ram.used,
                            total: item.ram.total,
                        }));
                        break;
                    case 'Storage':
                        chartData = data.map((item: any) => item.storage.percent);
                        chartPlot = data.map((item: any) => new Date(item.createdAt).toLocaleString());
                        additionalData = data.map((item: any) => ({
                            free: item.storage.free,
                            used: item.storage.used,
                            total: item.storage.total,
                        }));
                        break;
                    case 'Process Status':
                        if (selectedProcess) {
                            chartData = data.map((item: any) => ({
                                cpu: item.processStatus[selectedProcess].cpu_percent,
                                memory: item.processStatus[selectedProcess].memory_percent,
                            }));
                            chartPlot = data.map((item: any) => new Date(item.createdAt).toLocaleString());
                            additionalData = data.map((item: any) => ({
                                executionTimes: item.processStatus[selectedProcess].execution_times,
                            }));
                        }
                        break;
                    case 'Port Status':
                        chartData = data.map((item: any) => Object.keys(item.ports).map(port => (item.ports[port].status === 'UP' ? 1 : 0)));
                        chartPlot = data.map((item: any) => new Date(item.createdAt).toLocaleString());
                        additionalData = data.map((item: any) => ({
                            ports: item.ports,
                        }));
                        break;
                    default:
                        break;
                }

                setSelectedDetails(response.data);
                setChartData(chartData.flat());
                setChartPlot(chartPlot);
                setAdditionalData(additionalData);

            } catch (error) {
                setError('Error al cargar los detalles');
            }
        }
    };

    const exportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(records);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "HealthCheck");
        XLSX.writeFile(workbook, `HealthCheck_${new Date().toISOString()}.xlsx`);
    };

    if (loading) return <div>Cargando...</div>;
    if (error) return <div>{error}</div>;

    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = records.slice(indexOfFirstRecord, indexOfLastRecord);

    const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

    const handleRecordsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setRecordsPerPage(parseInt(e.target.value));
        setCurrentPage(1);
    };

    const getColorForPercentage = (percentage: number | null) => {
        if (percentage === null) return '';
        if (percentage > 75) return 'red-text';
        if (percentage > 50) return 'orange-text';
        return 'green-text';
    };

    const getColorForStatus = (status: string | null) => {
        if (status === null) return '';
        if (typeof status === 'string') {
            if (status.toLowerCase().includes('active')) return 'green-text';
            if (status.toLowerCase().includes('inactive')) return 'red-text';
        }
        return 'orange-text';
    };

    const getColorForStatusPort = (status: string | null) => {
        if (status === null) return '';
        if (status.toLowerCase().includes('closed')) return 'red-text';
        if (status.toLowerCase().includes('open')) return 'green-text';
        return 'orange-text';
    };

    const getColorForAlerts = (alert: string | null) => {
        if (alert === null) return '';
        if (alert.toLowerCase().includes('no alerts')) return 'green-text';
        if (alert.toLowerCase().includes('error')) return 'red-text';
        if (alert.toLowerCase().includes('alert')) return 'orange-text';
        return 'red-text';
    };

    const renderLoadingOrData = (data: any, mainStatus: string) => {
        if (mainStatus === 'UP' && data === null) {
            return <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>;
        }
        return data;
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', {
            month: 'numeric',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
            hour12: true,
        }).format(date);
    };

    const handleAutoRefreshToggle = () => {
        setAutoRefresh(!autoRefresh);
    };

    return (
        <>
            <div className='card'>
                <div className='card-body py-3'>
                    <div className='d-flex justify-content-between align-items-center'>
                        <div>
                            <Switch
                                checked={autoRefresh}
                                onChange={handleAutoRefreshToggle}
                                color="primary"
                            />
                            <span className="ms-2">Auto Refresh</span>
                        </div>
                        <div style={{ marginRight: '5px' }}>
                            <button className="btn-light-marino f-parrafo ms-3" style={{ flex: '0 0 10%', marginRight: '0px', marginBottom: '5px' }} onClick={exportToExcel}>
                                <i className="bi bi-download"></i>
                            </button>
                        </div>
                    </div>
                    <div className='tab-content'>
                        <div className='table-responsive' style={{ minHeight: '40vh' }}>
                            <HealthCheckTable
                                records={currentRecords}
                                handleRowClick={handleRowClick}
                                getColorForPercentage={getColorForPercentage}
                                getColorForStatus={getColorForStatus}
                                getColorForStatusPort={getColorForStatusPort}
                                getColorForAlerts={getColorForAlerts}
                                renderLoadingOrData={renderLoadingOrData}
                                formatDate={formatDate}
                            />
                        </div>
                        <br />
                        <div className="pagination-container">
                            <div className="row">
                                <div className="col-lg-4 col-md-6 col-sm-12">
                                    {records.length !== 0 && <div className="d-flex align-items-center">
                                        <label htmlFor="recordsPerPage" className="label-text me-2 mb-0 f-parrafo text-marino">Items per page:</label>
                                        <select
                                            id="recordsPerPage"
                                            className="form-select"
                                            value={recordsPerPage}
                                            onChange={handleRecordsPerPageChange}
                                            style={{ width: '80px' }}
                                        >
                                            <option value="5">5</option>
                                            <option value="10">10</option>
                                            <option value="15">20</option>
                                        </select>
                                    </div>}
                                </div>
                                <div className="col-lg-8 col-md-6 col-sm-12 mt-3 mt-md-0">
                                    <nav aria-label="Page navigation example">
                                        <ul className="pagination justify-content-end">
                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => paginate(currentPage - 1)} aria-label="Previous">
                                                    <span aria-hidden="true">&laquo;</span>
                                                    <span className="sr-only">Previous</span>
                                                </button>
                                            </li>
                                            {Array.from({ length: Math.ceil(records.length / recordsPerPage) }).map((_, index) => (
                                                <li key={index} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                                                    <button className="page-link" onClick={() => paginate(index + 1)}>{index + 1}</button>
                                                </li>
                                            ))}
                                            <li className={`page-item ${currentPage === Math.ceil(records.length / recordsPerPage) ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => paginate(currentPage + 1)} aria-label="Next">
                                                    <span aria-hidden="true">&raquo;</span>
                                                    <span className="sr-only">Next</span>
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal para seleccionar proceso */}
            <CustomModalStatus
                show={showProcessSelector}
                handleClose={() => setShowProcessSelector(false)}
                handleConfirm={() => setShowProcessSelector(false)}
                title="Select Process"
            >
                <select onChange={(e) => handleProcessSelect(e.target.value)} className="form-select">
                    <option value="">-- Select Process --</option>
                    {['process_bgp', 'process_sync', 'process_ha1', 'process_ha2'].map((process) => (
                        <option key={process} value={process}>
                            {process}
                        </option>
                    ))}
                </select>
            </CustomModalStatus>
            <HealthCheckCustomModal
                show={showModal}
                handleClose={handleModalClose}
                handleConfirm={handleModalConfirm}
                selectedProcess={selectedProcess}
                setSelectedProcess={setSelectedProcess}
                setChartData={setChartData}
                setChartPlot={setChartPlot}
                title={
                    selectedCell === 'Process Status' && selectedProcess
                        ? `${selectedProcess} Details`
                        : selectedCell && ['CPU', 'Storage', 'RAM'].includes(selectedCell)
                            ? `${selectedCell} Details`
                            : selectedCell
                                ? `Consult details of the ${selectedCell}`
                                : 'Default Title'
                }
                additionalData={additionalData}
                chartData={chartData}
                chartPlot={chartPlot}
                chartTitle={
                    selectedCell === 'Process Status' && selectedProcess
                        ? `Details of ${selectedProcess}`
                        : `Details of ${selectedCell ?? 'Unknown'}`
                }
                chartSubtitle={
                    selectedCell === 'Process Status' && selectedProcess
                        ? `This is the ${selectedProcess?.toLowerCase()} history.`
                        : `This is the ${selectedCell?.toLowerCase() ?? 'unknown'} history.`
                }
                yAxisTitle={
                    selectedCell === 'Port Status'
                        ? 'Port Status'
                        : `Avg ${selectedCell ?? 'Metric'} (%)`
                }
                selectedCell={selectedCell || 'Default Metric'}
            >
                <div></div>
            </HealthCheckCustomModal>
        </>
    );
};

export default HealthCheck;
